// ============================================
// CONNECTEURS DE BOÎTE MAIL — ÉCRAN
// ============================================
// Déclare les boîtes dont les messages deviennent des dossiers SAV, et dit en
// clair, pour chacune : si elle est lue, pourquoi elle ne l'est pas, et quoi
// faire. Remplace features/connectors/ConnecteursView.tsx (même présentation).
//
// Le secret client n'est jamais renvoyé par l'API : à la modification, un champ
// laissé vide signifie « inchangé ». C'est ce qui permet de rouvrir la fiche
// pour corriger une adresse sans avoir à ressaisir le secret.
//
// Boîte d'envoi : une seule boîte Microsoft peut envoyer les e-mails du SAV
// (Microsoft Graph, Mail.Send). L'interrupteur « Envoyer depuis cette boîte »
// la désigne, après confirmation ; le serveur retire alors ce rôle aux autres.
// « Envoyer un e-mail de test » vérifie l'envoi réel, avant ou après.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, HelpCircle, Inbox, Loader2, Mail, MailX, Plus,
  RefreshCw, Send, Trash2, XCircle,
} from 'lucide-react';
import { AdminApi } from '../../services/api';
import type { JournalEmailsConnecteur, MailConnecteurDetaille, EmailJournal } from '../../services/api';
import AuthContext from '../../contexts/AuthContext';
import { Badge, Button, Card, Input, Modal, Select } from '../../shared/ui';
import type { EntreeConnecteur, FournisseurMail } from '../../types';
import {
  CLASSE_TON, adresseValide, dateLisible, depuis, diagnostiquer, estBoiteEnvoi, expliquerErreurEnvoi,
  messageBasculeEnvoi, peutDevenirBoiteEnvoi, reglagesEnvoiActifs, resumerSynchro, resumerTest, resumerTestEnvoi,
  statutEmail, texteConfirmationBascule, texteConfirmationEnvoi,
} from './diagnostic';
import GuideMicrosoft from './GuideMicrosoft';

// Mobile et tablette : cibles tactiles d'au moins 40 px. À partir de lg
// (ordinateur), les classes s'annulent et le rendu reste celui d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';
/** Lien texte : zone de toucher agrandie sans décaler la mise en page. */
const LIEN_TACTILE = 'py-3 -my-3 lg:py-0 lg:my-0';
/** Rangée de boutons d'une fenêtre : empilés pleine largeur sur téléphone. */
// Sur téléphone, les boutons restent collés en bas de la fenêtre qui défile.
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end max-sm:sticky max-sm:-bottom-5 max-sm:z-10 max-sm:bg-white max-sm:-mx-5 max-sm:px-5 max-sm:pb-5 max-sm:pt-3 max-sm:-mb-5';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

const VIDE: EntreeConnecteur = {
  label: '',
  provider: 'MICROSOFT',
  mailbox: '',
  tenantId: '',
  clientId: '',
  clientSecret: '',
};

type Resultat = { ok: boolean; message: string; detail?: string };

interface Confirmation {
  titre: string;
  effet: string;
  bouton: string;
  danger?: boolean;
  agir: () => Promise<void>;
}

/** Fenêtre « Envoyer un e-mail de test » : destinataire et résultat. */
interface TestEnvoi {
  connecteur: MailConnecteurDetaille;
  to: string;
  enCours: boolean;
  resultat: Resultat | null;
}

/** Interrupteur (même dessin que dans Paramètres, aux couleurs bleues). */
const Interrupteur: React.FC<{
  actif: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}> = ({ actif, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={actif}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!actif)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sapin-500
      before:absolute before:-inset-2 before:content-[''] lg:before:content-none
      disabled:opacity-50 ${actif ? 'bg-sapin-600' : 'bg-slate-300'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
        ${actif ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

function enService(c: MailConnecteurDetaille): boolean {
  return c.status === 'ACTIF' || c.status === 'ERREUR';
}

/** Une ligne du journal des e-mails. */
const LigneEmail: React.FC<{ e: EmailJournal; onOuvrirDossier?: (id: string) => void }> = ({ e, onOuvrirDossier }) => {
  const s = statutEmail(e.status);
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-encre truncate">{e.subject || '(sans objet)'}</p>
          <p className="text-xs text-graphite truncate">
            {e.fromName ? `${e.fromName} <${e.fromAddress}>` : e.fromAddress} · {dateLisible(e.receivedAt)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={CLASSE_TON[s.ton]}>{s.libelle}</Badge>
          {e.ticketId && (onOuvrirDossier ? (
            <button
              type="button"
              onClick={() => onOuvrirDossier(e.ticketId!)}
              className={`text-xs font-semibold text-sapin-700 hover:text-sapin-900 ${LIEN_TACTILE}`}
            >
              Dossier {e.ticketRef ?? ''}
            </button>
          ) : (
            <span className="text-xs text-graphite">Dossier {e.ticketRef ?? ''}</span>
          ))}
        </div>
      </div>
      {e.statusReason && (e.status === 'ERREUR' || e.status === 'A_QUALIFIER' || e.status.startsWith('IGNORE') || e.status === 'ECARTE_IA') && (
        <p className={`mt-1 text-xs ${e.status === 'ERREUR' ? 'text-red-700' : 'text-graphite'}`}>
          Motif : {e.statusReason}
        </p>
      )}
    </li>
  );
};

/** Journal d'une boîte : à traiter d'abord, puis les derniers reçus. */
const JournalBoite: React.FC<{ id: string; onOuvrirDossier?: (id: string) => void }> = ({ id, onOuvrirDossier }) => {
  const [journal, setJournal] = useState<JournalEmailsConnecteur | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setJournal(await AdminApi.getMailConnectorEmails(id));
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement impossible');
    }
  }, [id]);

  useEffect(() => { void charger(); }, [charger]);

  if (erreur) {
    return <p className="mt-3 text-xs text-red-700">Journal indisponible : {erreur}</p>;
  }
  if (!journal) {
    return (
      <p className="mt-3 flex items-center gap-2 text-xs text-graphite">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des e-mails…
      </p>
    );
  }

  const { erreurs, aQualifier } = journal.compteurs;
  return (
    <div className="mt-4 space-y-4">
      <div>
        <p className="text-xs font-semibold text-encre">
          À traiter : {erreurs} en erreur, {aQualifier} à qualifier
        </p>
        {journal.aTraiter.length === 0 ? (
          <p className="mt-1 text-xs text-graphite">Aucun e-mail en attente d’un geste.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-trait">
            {journal.aTraiter.map(e => <LigneEmail key={e.id} e={e} onOuvrirDossier={onOuvrirDossier} />)}
          </ul>
        )}
      </div>
      <div>
        <p className="text-xs font-semibold text-encre">Derniers e-mails reçus</p>
        {journal.recents.length === 0 ? (
          <p className="mt-1 text-xs text-graphite">Aucun e-mail lu sur cette boîte pour le moment.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-trait">
            {journal.recents.map(e => <LigneEmail key={e.id} e={e} onOuvrirDossier={onOuvrirDossier} />)}
          </ul>
        )}
      </div>
    </div>
  );
};

const ConnecteursView: React.FC<{
  onOuvrirDossier?: (id: string) => void;
  /** Ouvre l'écran « Courrier écarté » (e-mails écartés par le tri, sans dossier). */
  onCourrierEcarte?: () => void;
  /** Destinataire proposé pour l'e-mail de test ; à défaut, l'utilisateur connecté. */
  emailUtilisateur?: string;
}> = ({ onOuvrirDossier, onCourrierEcarte, emailUtilisateur }) => {
  // Lu sans useAuth : l'écran reste utilisable (et testable) hors AuthProvider.
  const auth = useContext(AuthContext);
  const emailConnecte = emailUtilisateur ?? auth?.user?.email ?? '';

  const [connecteurs, setConnecteurs] = useState<MailConnecteurDetaille[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [resultat, setResultat] = useState<Record<string, Resultat>>({});
  const [journalOuvert, setJournalOuvert] = useState<Record<string, boolean>>({});
  const [aide, setAide] = useState(false);

  // Contexte lu dans les paramètres ; à défaut, aucune conclusion n'en est tirée.
  const [ingestionActive, setIngestionActive] = useState<boolean | undefined>(undefined);
  const [silenceMinutes, setSilenceMinutes] = useState<number | undefined>(undefined);
  /** Libellés des réglages d'envoi déjà actifs, cités avant de choisir la boîte d'envoi. */
  const [reglagesEnvoi, setReglagesEnvoi] = useState<string[] | undefined>(undefined);

  const [fiche, setFiche] = useState<EntreeConnecteur | null>(null);
  const [ficheId, setFicheId] = useState<string | null>(null);
  const [erreurFiche, setErreurFiche] = useState<string | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);

  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const [testEnvoi, setTestEnvoi] = useState<TestEnvoi | null>(null);

  useEffect(() => {
    void recharger();
    AdminApi.getSettings()
      .then(r => {
        const ingestion = r.parametres.find(p => p.cle === 'email.ingestion.actif');
        if (ingestion) setIngestionActive(ingestion.valeur === 'true');
        const silence = Number(r.parametres.find(p => p.cle === 'email.alerte_silence_minutes')?.valeur);
        if (Number.isFinite(silence) && silence > 0) setSilenceMinutes(silence);
        setReglagesEnvoi(reglagesEnvoiActifs(r.parametres));
      })
      .catch(() => { /* lecture facultative */ });
  }, []);

  async function recharger() {
    try {
      const r = await AdminApi.getMailConnectors();
      setConnecteurs(r.connecteurs);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement impossible');
    } finally {
      setChargement(false);
    }
  }

  function ouvrirCreation() {
    setFicheId(null);
    setFiche({ ...VIDE });
    setErreurFiche(null);
  }

  function ouvrirModification(c: MailConnecteurDetaille) {
    setFicheId(c.id);
    setFiche({
      label: c.label,
      provider: c.provider,
      mailbox: c.mailbox,
      tenantId: c.tenantId ?? '',
      clientId: c.clientId,
      clientSecret: '', // vide = inchangé ; le secret n'est jamais réaffiché
    });
    setErreurFiche(null);
  }

  async function enregistrer() {
    if (!fiche) return;
    setErreurFiche(null);
    setEnregistrement(true);
    try {
      if (ficheId) {
        await AdminApi.updateMailConnector(ficheId, fiche);
      } else {
        await AdminApi.createMailConnector(fiche);
      }
      setFiche(null);
      await recharger();
    } catch (e) {
      setErreurFiche(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setEnregistrement(false);
    }
  }

  function demanderBascule(c: MailConnecteurDetaille) {
    const t = texteConfirmationBascule(c);
    setConfirmation({
      ...t,
      agir: async () => {
        const r = await AdminApi.setMailConnectorActive(c.id, !enService(c));
        setConnecteurs(r.connecteurs);
        setResultat(x => ({
          ...x,
          [c.id]: { ok: true, message: enService(c) ? 'Boîte suspendue.' : 'Boîte activée. Lancez « Synchroniser maintenant » pour la lire tout de suite.' },
        }));
      },
    });
  }

  /** Désigner (ou retirer) la boîte d'envoi : toujours après confirmation. */
  function demanderEnvoi(c: MailConnecteurDetaille) {
    const actuelle = connecteurs.find(x => x.id !== c.id && estBoiteEnvoi(x)) ?? null;
    const voulu = !estBoiteEnvoi(c);
    setConfirmation({
      ...texteConfirmationEnvoi(c, actuelle, reglagesEnvoi),
      agir: async () => {
        await AdminApi.setMailConnectorEnvoi(c.id, voulu);
        // Le serveur a pu retirer le rôle à une autre boîte : liste complète.
        await recharger();
        setResultat(x => ({ ...x, [c.id]: { ok: true, message: messageBasculeEnvoi(c, voulu) } }));
      },
    });
  }

  function ouvrirTestEnvoi(c: MailConnecteurDetaille) {
    setTestEnvoi({ connecteur: c, to: emailConnecte, enCours: false, resultat: null });
  }

  async function envoyerTest() {
    if (!testEnvoi || testEnvoi.enCours) return;
    const { connecteur: c, to } = testEnvoi;
    setTestEnvoi(t => (t ? { ...t, enCours: true, resultat: null } : t));
    let resultatEnvoi: Resultat;
    try {
      const r = await AdminApi.testMailConnectorEnvoi(c.id, to);
      resultatEnvoi = { ok: true, message: resumerTestEnvoi({ to: r?.to || to }, c.mailbox) };
    } catch (e) {
      const code = (e as { code?: unknown })?.code;
      const ex = expliquerErreurEnvoi(
        typeof code === 'string' ? code : null,
        e instanceof Error ? e.message : null,
        c.mailbox,
      );
      resultatEnvoi = { ok: false, message: `${ex.cause} ${ex.action}`, detail: ex.detail };
    }
    setTestEnvoi(t => (t ? { ...t, enCours: false, resultat: resultatEnvoi } : t));
    setResultat(x => ({ ...x, [c.id]: { ok: resultatEnvoi.ok, message: resultatEnvoi.message } }));
  }

  function demanderSuppression(c: MailConnecteurDetaille) {
    setConfirmation({
      titre: `Supprimer « ${c.label} » ?`,
      effet: `La boîte ${c.mailbox} ne sera plus lue et ses identifiants seront effacés. Les dossiers déjà créés sont conservés.${
        estBoiteEnvoi(c) ? ' C’est la boîte d’envoi : les e-mails du SAV passeront par le serveur SMTP s’il est configuré, sinon ils ne seront pas envoyés.' : ''}`,
      bouton: 'Supprimer',
      danger: true,
      agir: async () => {
        await AdminApi.deleteMailConnector(c.id);
        await recharger();
      },
    });
  }

  async function confirmer() {
    if (!confirmation) return;
    setConfirmationEnCours(true);
    try {
      await confirmation.agir();
      setConfirmation(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Action impossible');
      setConfirmation(null);
    } finally {
      setConfirmationEnCours(false);
    }
  }

  async function tester(c: MailConnecteurDetaille) {
    setOccupe(c.id);
    setResultat(t => { const n = { ...t }; delete n[c.id]; return n; });
    try {
      const r = await AdminApi.testMailConnector(c.id);
      setResultat(t => ({ ...t, [c.id]: resumerTest(r) }));
      await recharger();
    } catch (e) {
      setResultat(t => ({ ...t, [c.id]: { ok: false, message: `Test impossible : ${e instanceof Error ? e.message : 'erreur'}` } }));
    } finally {
      setOccupe(null);
    }
  }

  async function synchroniser(c: MailConnecteurDetaille) {
    setOccupe(c.id);
    setResultat(t => { const n = { ...t }; delete n[c.id]; return n; });
    try {
      const r = await AdminApi.syncMailConnector(c.id);
      setResultat(t => ({ ...t, [c.id]: resumerSynchro(r) }));
      await recharger();
    } catch (e) {
      setResultat(t => ({ ...t, [c.id]: { ok: false, message: `Synchronisation impossible : ${e instanceof Error ? e.message : 'erreur'}` } }));
    } finally {
      setOccupe(null);
    }
  }

  if (chargement) {
    return (
      <div className="flex items-center justify-center py-20 text-graphite">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des connecteurs…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-encre">Boîtes mail auto-gérées</h1>
          <p className="text-sm text-graphite mt-1">
            Les messages reçus sur ces adresses deviennent des dossiers SAV. La boîte d’envoi sert
            aussi à répondre aux clients.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {onCourrierEcarte && (
            <Button variant="secondary" onClick={onCourrierEcarte} className={`grow sm:grow-0 ${TACTILE}`}
              title="E-mails reçus qui n’ont pas ouvert de dossier : notifications automatiques, avis de l’IA">
              <MailX className="w-4 h-4 mr-1.5" /> Courrier écarté
            </Button>
          )}
          <Button variant="secondary" onClick={() => setAide(a => !a)} className={`grow sm:grow-0 ${TACTILE}`}>
            <HelpCircle className="w-4 h-4 mr-1.5" /> Aide Microsoft 365
          </Button>
          <Button onClick={ouvrirCreation} className={`grow sm:grow-0 ${TACTILE}`}>
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter une boîte
          </Button>
        </div>
      </header>

      {aide && (
        <Card className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-semibold text-encre">Créer l’application Microsoft 365 (Entra ID)</h2>
              <p className="text-sm text-graphite mt-1">
                À faire une fois. La même application peut lire sav@ et assistance@, et envoyer depuis la boîte d’envoi.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAide(false)} className={TACTILE}>Fermer</Button>
          </div>
          <div className="mt-4"><GuideMicrosoft /></div>
        </Card>
      )}

      {ingestionActive === false && connecteurs.some(enService) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            L’interrupteur général « Créer des dossiers depuis les boîtes mail » est coupé dans
            Paramètres : aucune boîte n’est lue tant qu’il reste coupé.
            {connecteurs.some(estBoiteEnvoi) && ' Les réponses des clients aux e-mails envoyés par la boîte d’envoi ne sont pas lues non plus.'}
          </span>
        </div>
      )}

      {connecteurs.some(c => c.provider === 'MICROSOFT') && !connecteurs.some(estBoiteEnvoi) && (
        <div className="flex items-start gap-2 rounded-lg border border-trait bg-brume p-3 text-sm text-graphite">
          <Send className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Aucune boîte d’envoi : les e-mails du SAV partent par le serveur SMTP s’il est configuré,
            sinon ils ne sont pas envoyés. Pour envoyer et répondre depuis Microsoft 365, activez
            « Envoyer depuis cette boîte » sur la boîte du SAV.
          </span>
        </div>
      )}

      {erreur && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{erreur}</span>
        </div>
      )}

      {connecteurs.length === 0 ? (
        <Card className="py-14 text-center">
          <Mail className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 font-medium text-encre">Aucune boîte connectée</p>
          <p className="text-sm text-graphite mt-1">
            Ajoutez une adresse pour que ses messages ouvrent des dossiers automatiquement.
          </p>
          {!aide && (
            <button
              type="button"
              onClick={() => setAide(true)}
              className={`mt-3 text-sm font-semibold text-sapin-700 hover:text-sapin-900 ${TACTILE}`}
            >
              Comment créer l’application Microsoft 365 ?
            </button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {connecteurs.map(c => {
            const diag = diagnostiquer(c, { ingestionActive, silenceMinutes });
            const res = resultat[c.id];
            const travail = occupe === c.id;
            return (
              <Card key={c.id} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-encre">{c.label}</span>
                      <Badge className={CLASSE_TON[diag.ton]}>{diag.libelle}</Badge>
                      {estBoiteEnvoi(c) && (
                        <Badge className="bg-sapin-100 text-sapin-800">
                          <Send className="w-3 h-3" aria-hidden="true" />
                          Boîte d’envoi
                        </Badge>
                      )}
                      <Badge className="bg-brume text-graphite">
                        {c.provider === 'MICROSOFT' ? 'Microsoft 365' : 'Google'}
                      </Badge>
                    </div>
                    <p className="text-sm text-graphite mt-1 break-words">{c.mailbox}</p>

                    <div className="mt-2 text-sm">
                      <p className={diag.ton === 'erreur' ? 'text-red-800' : diag.ton === 'alerte' ? 'text-amber-900' : 'text-encre'}>
                        {diag.cause}
                      </p>
                      {diag.action && (
                        <p className="mt-0.5 text-graphite">
                          <span className="font-medium text-encre">À faire : </span>{diag.action}
                        </p>
                      )}
                      {c.lastError && (
                        <details className="mt-1 text-xs text-graphite">
                          <summary className="cursor-pointer">Détail technique</summary>
                          <p className="mt-1 flex items-start gap-1.5 text-red-700 break-all">
                            <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{c.lastError}</span>
                          </p>
                        </details>
                      )}
                    </div>

                    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-graphite sm:grid-cols-2">
                      <div>
                        <dt className="inline">Dernière synchronisation : </dt>
                        <dd className="inline">
                          {c.lastSyncAt ? `${dateLisible(c.lastSyncAt)} (${depuis(c.lastSyncAt)})` : 'jamais'}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline">E-mails pris en compte depuis : </dt>
                        <dd className="inline">{c.syncFrom ? dateLisible(c.syncFrom) : 'la prochaine activation'}</dd>
                      </div>
                      <div>
                        <dt className="inline">Temps réel (abonnement) : </dt>
                        <dd className="inline">
                          {c.subscriptionExpiresAt ? `jusqu’au ${dateLisible(c.subscriptionExpiresAt)}` : 'non actif (lecture par cycle)'}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline">Secret client : </dt>
                        <dd className="inline">{c.secretConfigure ? 'enregistré (jamais réaffiché)' : 'absent'}</dd>
                      </div>
                    </dl>

                    {res && (
                      <p
                        role="status"
                        className={`mt-2 flex items-start gap-1.5 text-xs ${res.ok ? 'text-encre' : 'text-red-700'}`}
                      >
                        {res.ok
                          ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-green-700" />
                          : <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                        <span>{res.message}</span>
                      </p>
                    )}
                  </div>

                  <div data-testid="actions-boite" className="flex items-center gap-2 shrink-0 flex-wrap w-full sm:w-auto">
                    <Button variant="ghost" size="sm" disabled={travail} onClick={() => tester(c)}
                      className={`grow sm:grow-0 ${TACTILE}`}
                      title="Vérifie que les identifiants permettent de lire la boîte">
                      <RefreshCw className={`w-4 h-4 mr-1 ${travail ? 'animate-spin' : ''}`} />
                      Tester la connexion
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={enService(c) ? 'Lire la boîte maintenant, sans attendre le cycle' : 'Activez la boîte pour pouvoir la synchroniser'}
                      disabled={travail || !enService(c)}
                      onClick={() => synchroniser(c)}
                      className={`grow sm:grow-0 ${TACTILE}`}
                    >
                      Synchroniser maintenant
                    </Button>
                    <Button
                      variant={enService(c) ? 'secondary' : 'success'}
                      size="sm"
                      disabled={travail}
                      onClick={() => demanderBascule(c)}
                      className={`grow sm:grow-0 ${TACTILE}`}
                    >
                      {enService(c) ? 'Suspendre' : 'Activer'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => ouvrirModification(c)} className={`grow sm:grow-0 ${TACTILE}`}>
                      Modifier
                    </Button>
                    <Button variant="danger" size="sm" disabled={travail} title="Supprimer la boîte" onClick={() => demanderSuppression(c)} className={`min-w-[40px] lg:min-w-0 ${TACTILE}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {c.provider === 'MICROSOFT' && (
                  <div data-testid="envoi-boite" className="mt-3 border-t border-trait pt-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <span className="mt-2 lg:mt-0.5">
                        <Interrupteur
                          actif={estBoiteEnvoi(c)}
                          disabled={travail || (!estBoiteEnvoi(c) && !peutDevenirBoiteEnvoi(c))}
                          onChange={() => demanderEnvoi(c)}
                          label="Envoyer depuis cette boîte"
                        />
                      </span>
                      <div className="min-w-0 text-sm">
                        <p className="font-medium text-encre">Envoyer depuis cette boîte</p>
                        <p className="text-xs text-graphite mt-0.5">
                          {estBoiteEnvoi(c)
                            ? `Les e-mails du SAV partent de ${c.mailbox} par Microsoft 365 ; les réponses restent dans la conversation du client.`
                            : !peutDevenirBoiteEnvoi(c)
                              ? 'Indisponible : activez d’abord la lecture de cette boîte. Les clients répondent à l’adresse d’envoi ; une boîte suspendue ne lirait pas leurs réponses.'
                              : 'Désactivé : cette boîte ne sert qu’à recevoir. Une seule boîte peut être la boîte d’envoi.'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={travail || !c.secretConfigure}
                      title={c.secretConfigure ? 'Envoie un e-mail réel depuis cette boîte' : 'Enregistrez d’abord le secret client'}
                      onClick={() => ouvrirTestEnvoi(c)}
                      className={`w-full sm:w-auto ${TACTILE}`}
                    >
                      <Send className="w-4 h-4 mr-1.5" />
                      Envoyer un e-mail de test
                    </Button>
                  </div>
                )}

                <div className="mt-3 border-t border-trait pt-3">
                  <button
                    type="button"
                    aria-expanded={!!journalOuvert[c.id]}
                    onClick={() => setJournalOuvert(j => ({ ...j, [c.id]: !j[c.id] }))}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold text-sapin-700 hover:text-sapin-900 ${LIEN_TACTILE}`}
                  >
                    <Inbox className="w-3.5 h-3.5" />
                    {journalOuvert[c.id] ? 'Masquer les e-mails reçus' : 'Voir les e-mails reçus'}
                    {journalOuvert[c.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {journalOuvert[c.id] && <JournalBoite id={c.id} onOuvrirDossier={onOuvrirDossier} />}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={confirmation !== null}
        onClose={() => { if (!confirmationEnCours) setConfirmation(null); }}
        title={confirmation?.titre ?? ''}
        size="sm"
      >
        {confirmation && (
          <div className="space-y-4">
            <p className="text-sm text-graphite">{confirmation.effet}</p>
            <div className={ACTIONS_FENETRE}>
              <Button variant="secondary" disabled={confirmationEnCours} onClick={() => setConfirmation(null)} className={BOUTON_FENETRE}>Annuler</Button>
              <Button variant={confirmation.danger ? 'danger' : 'primary'} disabled={confirmationEnCours} onClick={confirmer} className={BOUTON_FENETRE}>
                {confirmationEnCours && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {confirmation.bouton}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={testEnvoi !== null}
        onClose={() => { if (!testEnvoi?.enCours) setTestEnvoi(null); }}
        title="Envoyer un e-mail de test"
        size="sm"
      >
        {testEnvoi && (
          <form
            className="space-y-4"
            onSubmit={e => { e.preventDefault(); void envoyerTest(); }}
          >
            <p className="text-sm text-graphite">
              Un e-mail réel part de <span className="font-medium text-encre">{testEnvoi.connecteur.mailbox}</span> par
              Microsoft 365. Il vérifie l’autorisation Mail.Send et la stratégie d’accès, sans rien changer aux réglages.
            </p>
            <label className="block">
              <span className="text-sm font-medium text-encre">Destinataire</span>
              <Input
                type="email"
                value={testEnvoi.to}
                placeholder="vous@klygroupe.com"
                disabled={testEnvoi.enCours}
                onChange={e => setTestEnvoi({ ...testEnvoi, to: e.target.value, resultat: null })}
                className="mt-1 w-full"
              />
            </label>
            {testEnvoi.resultat && (
              <div
                role={testEnvoi.resultat.ok ? 'status' : 'alert'}
                className={`rounded-lg border p-3 text-sm ${testEnvoi.resultat.ok
                  ? 'border-green-200 bg-green-50 text-green-900'
                  : 'border-red-200 bg-red-50 text-red-800'}`}
              >
                <p>{testEnvoi.resultat.message}</p>
                {testEnvoi.resultat.detail && (
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer">Détail technique</summary>
                    <p className="mt-1 break-all">{testEnvoi.resultat.detail}</p>
                  </details>
                )}
              </div>
            )}
            <div className={ACTIONS_FENETRE}>
              <Button variant="secondary" type="button" disabled={testEnvoi.enCours} onClick={() => setTestEnvoi(null)} className={BOUTON_FENETRE}>
                Fermer
              </Button>
              <Button type="submit" disabled={testEnvoi.enCours || !adresseValide(testEnvoi.to)} className={BOUTON_FENETRE}>
                {testEnvoi.enCours && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                Envoyer
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={fiche !== null}
        onClose={() => setFiche(null)}
        title={ficheId ? 'Modifier la boîte' : 'Ajouter une boîte'}
        size="lg"
      >
        {fiche && (
          <div className="space-y-4">
            {erreurFiche && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {erreurFiche}
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-encre">Nom</span>
              <Input
                value={fiche.label}
                placeholder="SAV principal"
                onChange={e => setFiche({ ...fiche, label: e.target.value })}
                className="mt-1 w-full"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-encre">Fournisseur</span>
              <Select
                value={fiche.provider}
                options={[
                  { value: 'MICROSOFT', label: 'Microsoft 365 / Outlook' },
                  { value: 'GOOGLE', label: 'Google Workspace' },
                ]}
                onChange={e => setFiche({ ...fiche, provider: e.target.value as FournisseurMail })}
                className="mt-1 w-full"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-encre">Adresse de la boîte</span>
              <Input
                type="email"
                value={fiche.mailbox}
                placeholder="sav@klygroupe.com"
                onChange={e => setFiche({ ...fiche, mailbox: e.target.value })}
                className="mt-1 w-full"
              />
            </label>

            {fiche.provider === 'MICROSOFT' && (
              <label className="block">
                <span className="text-sm font-medium text-encre">Tenant Azure AD</span>
                <Input
                  value={fiche.tenantId ?? ''}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  onChange={e => setFiche({ ...fiche, tenantId: e.target.value })}
                  className="mt-1 w-full"
                />
                <span className="mt-1 block text-xs text-graphite">
                  « ID de l’annuaire (locataire) » sur la page « Vue d’ensemble » de l’application.
                </span>
              </label>
            )}

            <label className="block">
              <span className="text-sm font-medium text-encre">Identifiant de l'application</span>
              <Input
                value={fiche.clientId}
                onChange={e => setFiche({ ...fiche, clientId: e.target.value })}
                className="mt-1 w-full"
              />
              {fiche.provider === 'MICROSOFT' && (
                <span className="mt-1 block text-xs text-graphite">
                  « ID d’application (client) » sur la même page.
                </span>
              )}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-encre">Secret client</span>
              <Input
                type="password"
                name="secret-client-connecteur"
                autoComplete="new-password"
                value={fiche.clientSecret ?? ''}
                placeholder={ficheId ? 'Laisser vide pour ne pas changer' : ''}
                onChange={e => setFiche({ ...fiche, clientSecret: e.target.value })}
                className="mt-1 w-full"
              />
              <span className="mt-1 block text-xs text-graphite">
                Collez la « Valeur » du secret, pas son « ID ». Stocké chiffré, il n'est jamais réaffiché ensuite.
              </span>
            </label>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>À ne pas oublier côté Microsoft :</strong> les autorisations d'application
              <code className="mx-1">Mail.Read</code>, <code className="mx-1">Mail.ReadWrite</code> et
              <code className="mx-1">Mail.Send</code> donnent accès à <em>toutes</em> les boîtes du
              tenant, en lecture comme en envoi. Restreignez-les aux boîtes du SAV avec une
              <em> Application Access Policy</em>. Le détail est dans « Aide Microsoft 365 ».
            </div>

            <div className={`${ACTIONS_FENETRE} pt-2`}>
              <Button variant="secondary" onClick={() => setFiche(null)} className={BOUTON_FENETRE}>Annuler</Button>
              <Button disabled={enregistrement} onClick={enregistrer} className={BOUTON_FENETRE}>
                {enregistrement && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {ficheId ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ConnecteursView;
